import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Alert, Button, Col, Form, Row } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import RazorpaySettings from './RazorpaySettings';
import ShippingSettings from './ShippingSettings';
import GrievanceSettings from './GrievanceSettings';
import DirectorySettings from './DirectorySettings';
import StoreReadiness from './StoreReadiness';
import PhoneNumberInput from './PhoneNumberInput';

const EMPTY = {
  enabled: false, slug: '', storeName: '', tagline: '', about: '', contactEmail: '', contactPhone: '', returnsPolicy: '', shippingPolicy: '', terms: '',
  pricesIncludeGst: false, warehouseId: '', acceptCod: false, acceptUpi: false, acceptBankTransfer: false, paymentNote: '', shippingFlat: '', freeShippingOver: '', minOrderAmount: '', unpaidHoldHours: '', listed: true
};

/** Org Admin > Online Store: switch the store on, name and address, contact and policies, how buyers can pay, shipping charges. */
function OnlineStoreSettings() {
  const { refreshDateFormat, user } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [readyKey, setReadyKey] = useState(0); // asks the "ready to announce" list to look again after a save
  const [meta, setMeta] = useState({ planIncludesStore: true, shopBaseUrl: 'https://ibigdo.com/shop/', saved: false });
  const [warehouses, setWarehouses] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, w] = await Promise.all([axios.get('/api/store/settings'), axios.get('/api/inventory/warehouses')]);
      const d = s.data || {};
      setMeta({ planIncludesStore: d.planIncludesStore !== false, shopBaseUrl: d.shopBaseUrl || 'https://ibigdo.com/shop/', saved: Boolean(d.saved) });
      setForm(Object.fromEntries(Object.keys(EMPTY).map((k) => [k, d[k] ?? EMPTY[k]])));
      setWarehouses((w.data || []).filter((x) => x.isActive !== false));
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not load the online store settings.');
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const number = (v) => (v === '' || v == null ? null : Number(v));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put('/api/store/settings', {
        ...form,
        warehouseId: form.warehouseId === '' ? null : Number(form.warehouseId),
        shippingFlat: number(form.shippingFlat), freeShippingOver: number(form.freeShippingOver), minOrderAmount: number(form.minOrderAmount), unpaidHoldHours: number(form.unpaidHoldHours)
      });
      toast.success(form.enabled ? 'Saved. Your online store is open.' : 'Saved.');
      setReadyKey((k) => k + 1);
      await load();
      if (user?.organizationId) refreshDateFormat(user.organizationId); // refreshes whether the Online Store is in the menu
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return null;
  const address = `${meta.shopBaseUrl}${form.slug || 'your-store'}`;

  return (
    <>
    {meta.saved && <StoreReadiness refreshKey={readyKey} />}
    <Form onSubmit={save} className="mt-3" style={{ maxWidth: 820 }} data-testid="online-store-settings">
      <p className="text-muted">
        Sell your products to buyers on a page of your own. Publish products from the Online Store menu once it is switched on; buyers see
        only what you publish.
      </p>
      {!meta.planIncludesStore && <Alert variant="warning">Your plan doesn't include the online store. Ask us to move you to a plan that does.</Alert>}

      <Form.Check type="switch" id="store-enabled" className="mb-3" label="We sell online" checked={form.enabled} onChange={set('enabled')} data-testid="store-enabled" />

      <h6>Your store</h6>
      <Row className="g-3 mb-3">
        <Col md={6}><Form.Group><Form.Label>Store name</Form.Label><Form.Control value={form.storeName} onChange={set('storeName')} maxLength={120} /></Form.Group></Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Store address</Form.Label>
            <Form.Control value={form.slug} onChange={set('slug')} maxLength={40} placeholder="your-store" data-testid="store-slug" />
            <Form.Text className="text-muted">{address}. Small letters, numbers and hyphens.</Form.Text>
          </Form.Group>
        </Col>
        <Col md={12}>
          <Form.Check type="switch" id="store-listed" label="Let buyers find my store by name on ibigdo.com/shop" checked={form.listed !== false} onChange={set('listed')} data-testid="store-listed" />
          <Form.Text className="text-muted">When someone types your store's name on ibigdo.com/shop, it is suggested. Your store address works either way; switch this off if you only share the link yourself.</Form.Text>
        </Col>
        <Col md={12}><Form.Group><Form.Label>Tagline</Form.Label><Form.Control value={form.tagline} onChange={set('tagline')} maxLength={200} /></Form.Group></Col>
        <Col md={12}><Form.Group><Form.Label>About your business</Form.Label><Form.Control as="textarea" rows={3} value={form.about} onChange={set('about')} /></Form.Group></Col>
        <Col md={6}><Form.Group><Form.Label>Contact email</Form.Label><Form.Control type="email" value={form.contactEmail} onChange={set('contactEmail')} /></Form.Group></Col>
        <Col md={6}><Form.Group><Form.Label>Contact phone</Form.Label><PhoneNumberInput name="contactPhone" value={form.contactPhone} onChange={set('contactPhone')} /></Form.Group></Col>
      </Row>

      <h6>Stock and prices</h6>
      <Row className="g-3 mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Sell from warehouse</Form.Label>
            <Form.Select value={form.warehouseId} onChange={set('warehouseId')}>
              <option value="">{warehouses.length === 1 ? `${warehouses[0].name} (the only one)` : 'Choose a warehouse'}</option>
              {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </Form.Select>
            <Form.Text className="text-muted">Buyers see "in stock" from this warehouse.</Form.Text>
          </Form.Group>
        </Col>
        <Col md={6} className="d-flex align-items-center">
          <Form.Check type="switch" id="store-gst" label="Prices I set already include GST" checked={form.pricesIncludeGst} onChange={set('pricesIncludeGst')} />
        </Col>
      </Row>

      <h6>How buyers can pay</h6>
      <div className="mb-2">
        <Form.Check inline type="checkbox" id="pay-upi" label="UPI" checked={form.acceptUpi} onChange={set('acceptUpi')} />
        <Form.Check inline type="checkbox" id="pay-bank" label="Bank transfer" checked={form.acceptBankTransfer} onChange={set('acceptBankTransfer')} />
        <Form.Check inline type="checkbox" id="pay-cod" label="Cash on delivery" checked={form.acceptCod} onChange={set('acceptCod')} />
      </div>
      <Form.Group className="mb-3">
        <Form.Label>Note to buyers about payment</Form.Label>
        <Form.Control value={form.paymentNote} onChange={set('paymentNote')} placeholder="For example: pay by UPI within 24 hours of ordering" />
      </Form.Group>

      <h6>Unpaid orders</h6>
      <Form.Group className="mb-3" style={{ maxWidth: 420 }}>
        <Form.Label>Cancel an unpaid order after (hours)</Form.Label>
        <Form.Control type="number" min="1" max="720" step="1" value={form.unpaidHoldHours} onChange={set('unpaidHoldHours')} placeholder="Empty: keep until I cancel it" data-testid="unpaid-hold-hours" />
        <Form.Text className="text-muted">An order paid by UPI or bank transfer reserves its stock from the moment it is placed. If it is still unpaid after this many hours it is cancelled, the stock is freed and the buyer is told. Cash on delivery orders and paid orders are never cancelled this way. 24 is one day, 72 is three.</Form.Text>
      </Form.Group>

      <h6>Shipping</h6>
      <Row className="g-3 mb-3">
        <Col md={4}><Form.Group><Form.Label>Shipping charge</Form.Label><Form.Control type="number" min="0" step="0.01" value={form.shippingFlat} onChange={set('shippingFlat')} /></Form.Group></Col>
        <Col md={4}><Form.Group><Form.Label>Free shipping over</Form.Label><Form.Control type="number" min="0" step="0.01" value={form.freeShippingOver} onChange={set('freeShippingOver')} /></Form.Group></Col>
        <Col md={4}><Form.Group><Form.Label>Smallest order</Form.Label><Form.Control type="number" min="0" step="0.01" value={form.minOrderAmount} onChange={set('minOrderAmount')} /></Form.Group></Col>
      </Row>

      <h6>Policies shown to buyers</h6>
      <Form.Group className="mb-2"><Form.Label>Shipping and delivery</Form.Label><Form.Control as="textarea" rows={2} value={form.shippingPolicy} onChange={set('shippingPolicy')} /></Form.Group>
      <Form.Group className="mb-2"><Form.Label>Returns and refunds</Form.Label><Form.Control as="textarea" rows={2} value={form.returnsPolicy} onChange={set('returnsPolicy')} /></Form.Group>
      <Form.Group className="mb-3"><Form.Label>Terms of sale</Form.Label><Form.Control as="textarea" rows={2} value={form.terms} onChange={set('terms')} /></Form.Group>

      <Button type="submit" disabled={saving} data-testid="store-save">{saving ? 'Saving...' : 'Save'}</Button>
      {form.enabled && form.slug && meta.saved && (
        <a className="ms-3" href={`/shop/${form.slug}`} target="_blank" rel="noreferrer">Open my store <i className="bi bi-box-arrow-up-right" /></a>
      )}
    </Form>
    <div style={{ maxWidth: 820 }}><ShippingSettings storeSaved={meta.saved} /></div>
    <div style={{ maxWidth: 820 }}><GrievanceSettings storeSaved={meta.saved} onSaved={() => setReadyKey((k) => k + 1)} /></div>
    <div style={{ maxWidth: 820 }}><DirectorySettings key={readyKey} storeSaved={meta.saved} /></div>
    <div style={{ maxWidth: 820 }}><RazorpaySettings storeSaved={meta.saved} /></div>
    </>
  );
}

export default OnlineStoreSettings;
